import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { ActionIcon, Box, Button, Divider, Group, Popover, Select, Stack, Text, Tooltip } from '@mantine/core';
import {
  IconChevronDown,
  IconLanguage,
  IconPlus,
  IconRotateClockwise,
  IconStar,
  IconStarFilled,
  IconTrash,
  IconX,
} from '@tabler/icons-react';
import type { FlowVoiceConfig, LanguageConfig, LanguageEntry } from '@legacy-shared/widgets/core/types';
import { FlowVoiceSelector } from './flow-voice-selector';

const availableLanguages = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Spanish' },
  { code: 'fr', label: 'French' },
  { code: 'de', label: 'German' },
  { code: 'pt', label: 'Portuguese' },
  { code: 'it', label: 'Italian' },
  { code: 'zh', label: 'Chinese' },
  { code: 'ja', label: 'Japanese' },
  { code: 'ko', label: 'Korean' },
  { code: 'ar', label: 'Arabic' },
  { code: 'hi', label: 'Hindi' },
  { code: 'ru', label: 'Russian' },
  { code: 'vi', label: 'Vietnamese' },
  { code: 'tl', label: 'Tagalog' },
  { code: 'pl', label: 'Polish' },
];

const maxTriggerCodes = 3;

function CodeTag({ children }: { children: string }) {
  return (
    <Box
      component="span"
      style={{
        fontFamily: 'var(--mantine-font-family-monospace)',
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: 0.8,
        padding: '2px 5px',
        borderRadius: 3,
        border: '1px solid var(--mantine-color-default-border)',
        background: 'var(--mantine-color-default-hover)',
        color: 'var(--mantine-color-dimmed)',
        textTransform: 'uppercase',
        lineHeight: 1.2,
      }}
    >
      {children}
    </Box>
  );
}

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <Text size="xs" fw={600} c="dimmed" tt="uppercase" style={{ letterSpacing: 0.6 }}>
      {children}
    </Text>
  );
}

function VoiceSummary({ voice }: { voice: FlowVoiceConfig }) {
  return (
    <Text component="span" size="sm" fw={500} truncate style={{ maxWidth: 140 }}>
      {voice.voiceName}
    </Text>
  );
}

function LanguageSummary({ languages }: { languages: LanguageEntry[] }) {
  const visible = languages.slice(0, maxTriggerCodes);
  const overflow = languages.length - visible.length;

  return (
    <Box component="span" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      {visible.map((language) => (
        <CodeTag key={language.code}>{language.code}</CodeTag>
      ))}
      {overflow > 0 && (
        <Text component="span" size="xs" c="dimmed" fw={500}>
          +{overflow}
        </Text>
      )}
    </Box>
  );
}

type LanguageRowProps = {
  entry: LanguageEntry;
  isDefault: boolean;
  flowVoiceConfig: FlowVoiceConfig;
  elevenLabsAvailable: boolean;
  onMakeDefault: () => void;
  onRemove: () => void;
  onChangeVoice: (config: FlowVoiceConfig) => void;
  onResetVoice: () => void;
};

function LanguageRow({
  entry,
  isDefault,
  flowVoiceConfig,
  elevenLabsAvailable,
  onMakeDefault,
  onRemove,
  onChangeVoice,
  onResetVoice,
}: LanguageRowProps) {
  return (
    <Stack gap={6}>
      <Group justify="space-between" wrap="nowrap" gap="xs">
        <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
          <CodeTag>{entry.code}</CodeTag>
          <Text size="sm" fw={500} truncate>
            {entry.label}
          </Text>
        </Group>
        <Group gap={2} wrap="nowrap">
          <Tooltip label={isDefault ? 'Default language' : 'Set as default'} withArrow openDelay={400}>
            <ActionIcon
              variant="subtle"
              size="sm"
              color={isDefault ? 'grape' : 'gray'}
              onClick={isDefault ? undefined : onMakeDefault}
              style={{ cursor: isDefault ? 'default' : 'pointer' }}
            >
              {isDefault ? <IconStarFilled size={14} /> : <IconStar size={14} />}
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Remove language" withArrow openDelay={400}>
            <ActionIcon variant="subtle" size="sm" color="red" onClick={onRemove}>
              <IconTrash size={14} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>

      <Group gap="xs" wrap="nowrap" pl={32}>
        <FlowVoiceSelector
          voiceConfig={entry.voiceConfig ?? flowVoiceConfig}
          onChange={onChangeVoice}
          elevenLabsAvailable={elevenLabsAvailable}
          languageFilter={entry.code}
        />
        {entry.voiceConfig ? (
          <Tooltip label="Reset to flow default voice" withArrow openDelay={400}>
            <ActionIcon variant="subtle" size="sm" onClick={onResetVoice}>
              <IconRotateClockwise size={14} />
            </ActionIcon>
          </Tooltip>
        ) : (
          <Text size="xs" c="dimmed" fs="italic">
            inherits default
          </Text>
        )}
      </Group>
    </Stack>
  );
}

type FlowVoiceLanguageControlProps = {
  voiceConfig: FlowVoiceConfig;
  onVoiceConfigChange: (config: FlowVoiceConfig) => void;
  languageConfig?: LanguageConfig;
  onLanguageConfigChange: (config: LanguageConfig | undefined) => void;
  elevenLabsAvailable: boolean;
};

export function FlowVoiceLanguageControl({
  voiceConfig,
  onVoiceConfigChange,
  languageConfig,
  onLanguageConfigChange,
  elevenLabsAvailable,
}: FlowVoiceLanguageControlProps) {
  const [opened, setOpened] = useState(false);

  const languages = useMemo(() => languageConfig?.languages ?? [], [languageConfig]);
  const defaultLanguage = languageConfig?.defaultLanguage ?? '';
  const isMultilingual = languages.length > 0;

  const availableToAdd = useMemo(() => {
    const used = new Set(languages.map((l) => l.code));

    return availableLanguages.filter((l) => !used.has(l.code));
  }, [languages]);

  const commitLanguages = (next: LanguageEntry[], nextDefault?: string) => {
    const first = next[0];

    if (!first) {
      onLanguageConfigChange(undefined);

      return;
    }

    const fallback = nextDefault ?? defaultLanguage;

    onLanguageConfigChange({
      languages: next,
      defaultLanguage: next.some((l) => l.code === fallback) ? fallback : first.code,
    });
  };

  const setLanguageVoice = (code: string, override: FlowVoiceConfig | undefined) => {
    commitLanguages(languages.map((l) => (l.code === code ? { ...l, voiceConfig: override } : l)));
  };

  const addLanguage = (code: string, replaceExisting: boolean) => {
    const language = availableLanguages.find((l) => l.code === code);

    if (!language) return;

    const entry = { code: language.code, label: language.label };

    if (replaceExisting) {
      onLanguageConfigChange({ languages: [entry], defaultLanguage: entry.code });

      return;
    }

    commitLanguages([...languages, entry], defaultLanguage || entry.code);
  };

  return (
    <Popover
      width={460}
      position="bottom-start"
      shadow="md"
      withArrow
      opened={opened}
      onChange={setOpened}
      closeOnClickOutside={false}
      closeOnEscape
      trapFocus={false}
    >
      <Popover.Target>
        <Button
          variant="subtle"
          size="xs"
          color={isMultilingual ? 'grape' : undefined}
          leftSection={<IconLanguage size={16} />}
          rightSection={<IconChevronDown size={12} />}
          onClick={() => setOpened((o) => !o)}
        >
          {isMultilingual ? <LanguageSummary languages={languages} /> : <VoiceSummary voice={voiceConfig} />}
        </Button>
      </Popover.Target>

      <Popover.Dropdown p={0}>
        <Group
          justify="space-between"
          align="center"
          wrap="nowrap"
          px="md"
          py="xs"
          style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}
        >
          <SectionLabel>{isMultilingual ? 'Languages & Voices' : 'Flow Voice'}</SectionLabel>
          <ActionIcon variant="subtle" size="sm" onClick={() => setOpened(false)} aria-label="Close">
            <IconX size={14} />
          </ActionIcon>
        </Group>

        <Stack gap="sm" p="md">
          {!isMultilingual && (
            <>
              <FlowVoiceSelector
                voiceConfig={voiceConfig}
                onChange={onVoiceConfigChange}
                elevenLabsAvailable={elevenLabsAvailable}
              />
              <Divider />
              <Stack gap={6}>
                <SectionLabel>Multilingual</SectionLabel>
                <Text size="xs" c="dimmed">
                  Add a language to support multilingual callers. The flow voice becomes the default for the first
                  language.
                </Text>
                <Select
                  size="xs"
                  placeholder="Add a language..."
                  data={availableLanguages.map((l) => ({ value: l.code, label: l.label }))}
                  value={null}
                  onChange={(v) => v && addLanguage(v, true)}
                  searchable
                  clearable={false}
                  leftSection={<IconPlus size={14} />}
                />
              </Stack>
            </>
          )}

          {isMultilingual && (
            <>
              <Stack gap="md">
                {languages.map((language, i) => (
                  <Box key={language.code}>
                    {i > 0 && <Divider mb="md" />}
                    <LanguageRow
                      entry={language}
                      isDefault={language.code === defaultLanguage}
                      flowVoiceConfig={voiceConfig}
                      elevenLabsAvailable={elevenLabsAvailable}
                      onMakeDefault={() => commitLanguages(languages, language.code)}
                      onRemove={() => commitLanguages(languages.filter((l) => l.code !== language.code))}
                      onChangeVoice={(config) => setLanguageVoice(language.code, config)}
                      onResetVoice={() => setLanguageVoice(language.code, undefined)}
                    />
                  </Box>
                ))}
              </Stack>

              {availableToAdd.length > 0 && (
                <>
                  <Divider />
                  <Select
                    size="xs"
                    placeholder="Add a language..."
                    data={availableToAdd.map((l) => ({ value: l.code, label: l.label }))}
                    value={null}
                    onChange={(v) => v && addLanguage(v, false)}
                    searchable
                    clearable={false}
                    leftSection={<IconPlus size={14} />}
                  />
                </>
              )}
            </>
          )}
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
